/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Home Builders",
  title: "AI for home builders",
  titleHighlight: "from lot to closing.",
  description: `One platform where AI helps home builders bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for home builders",
    items: [
      {
        icon: Sparkles,
        title: "Whole-house estimating",
        description:
          "Lot to framing to finish — every cost category tracked separately.",
      },
      {
        icon: Sparkles,
        title: "Trade coordination",
        description:
          "Sub bids, sub schedules, and sub payments in one workspace.",
      },
      {
        icon: Sparkles,
        title: "Allowance tracking",
        description:
          "Client-selected finishes and fixtures tracked against allowance budgets.",
      },
      {
        icon: Sparkles,
        title: "Change order management",
        description:
          "Every change priced, approved, and tracked against the original contract.",
      },
      {
        icon: Sparkles,
        title: "Construction loan tracking",
        description:
          "Draw requests and lender-required documentation organized per project.",
      },
      {
        icon: Sparkles,
        title: "Hank for builders",
        description:
          "'What's my margin on custom homes this year?' Real answer from real closings.",
      },
    ],
  },

  workflow: {
    title: "How it works for home builders",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Pre-con",
        title: "From lot purchase to signed contract",
        description:
          "Build the whole-house estimate, coordinate sub bids, track allowances, and draft the contract. AI matches every line to your cost database and flags scope gaps.",
        details: [],
      },
      {
        label: "Build",
        title: "Project management through framing to finish",
        description:
          "Accepted contract becomes a project with phase-by-phase budget tracking, sub schedules, and change order management. Real-time visibility into where the job stands.",
        details: [],
      },
      {
        label: "Close",
        title: "Draws, punch lists, and final accounting",
        description:
          "Draw requests, punch list tracking, and final close-out documentation in one system. Hank can draft draw request summaries for the lender on demand.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Whole-house estimating, not just one trade",
      description:
        "Custom home builders juggle 20+ cost categories per job. Contractor Co-Pilot handles all of them in one workspace — framing, roofing, HVAC, electrical, plumbing, finishes, landscaping, hardscape. No per-trade tool sprawl.",
      bullets: [
        "20+ cost categories per project",
        "Multi-trade cost database pre-loaded",
        "Per-category budget tracking",
        "Phase-based cost allocation",
      ],
    },
    {
      icon: Sparkles,
      title: "Allowance and change order tracking built in",
      description:
        "Custom homes live or die on allowances and change orders. Contractor Co-Pilot tracks allowance items against client selections and flags every change order for approval before it becomes an invoice.",
      bullets: [
        "Allowance budgets per category",
        "Client-selected finish tracking",
        "Change order approval workflow",
        "Variance reporting against contract",
      ],
    },
    {
      icon: Sparkles,
      title: "Sub coordination without spreadsheet chaos",
      description:
        "Track sub bids, sub schedules, sub payments, and sub insurance all in one system. Know which subs are on budget, which are behind schedule, and who's overdue on a payment.",
      bullets: [
        "Sub bid tracking per scope",
        "Schedule conflicts surfaced",
        "Payment tracking per sub",
        "Insurance and license expiration alerts",
      ],
    },
    {
      icon: Sparkles,
      title: "Draw requests and lender documentation",
      description:
        "Construction loans mean monthly draw requests. Contractor Co-Pilot organizes completed work, receipts, and lien waivers per draw so you're not scrambling to assemble a package at the end of the month.",
      bullets: [
        "Draw package organization",
        "Lien waiver tracking",
        "Lender documentation templates",
        "Hank-drafted draw summaries",
      ],
    },
  ],

  relatedFeatures: [
    {
      label: "Residential Construction Solutions",
      href: "/solutions/residential-construction",
      icon: Ruler,
    },
    {
      label: "Drywall Calculator",
      href: "/tools/drywall-calculator",
      icon: Calculator,
    },
    {
      label: "How to Price a Construction Project",
      href: "/blog/how-to-price-a-construction-project",
      icon: BookOpen,
    },
    { label: "CC vs STACK", href: "/compare/stack", icon: BarChart3 },
    {
      label: "Budgets & Invoicing",
      href: "/features/budgets-invoices",
      icon: Receipt,
    },
    { label: "Change Orders", href: "/features/change-orders", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForHomeBuildersPageClient() {
  return <FeaturePageTemplate config={config} />;
}
